import { useCallback, useEffect, useState } from 'react';
import { saveOrder, setItemStatus, type CatalogItem, type Order } from '@makila/pos-core';
import { ChefHat, X } from 'lucide-react';
import clsx from 'clsx';
import { ManagerApproval } from '../components/ManagerApproval';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';

const minutes = (iso: string) => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));

/**
 * Ecrã da cozinha/bar (KDS): os pedidos enviados pelas mesas, por estação, do
 * mais antigo para o mais recente. Tocar num item marca-o pronto; a sala vê-o
 * na mesa. Precisa de rede para ver os pedidos das outras caixas (chegam pela
 * nuvem, a cada 5 s); sem rede, os talões da cozinha continuam a sair na sala.
 */
export function KitchenScreen(p: { store: IdbTerminalStore; device: DeviceState; sync: SyncStatus | null; onSyncNow: () => void; onExit: () => void }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stations, setStations] = useState<string[]>([]);
  const [station, setStation] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [, tick] = useState(0);
  const reload = useCallback(async () => {
    setOrders((await p.store.orders()).filter((o) => o.status === 'open'));
    const cats = (await p.store.catalog('category')).map((i: CatalogItem) => i.data.station as string | null).filter((x): x is string => !!x);
    setStations([...new Set(cats)].sort());
  }, [p.store]);
  useEffect(() => { void reload(); }, [reload, p.sync?.lastSyncAt]);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);

  const mine = (s: string | null) => !station || (s ?? 'Cozinha') === station;
  const cards = orders.map((o) => ({ o, items: o.items.filter((i) => (i.status === 'sent' || i.status === 'ready') && !i.paidIn && mine(i.station)) }))
    .filter((c) => c.items.some((i) => i.status === 'sent'))
    .sort((a, b) => Math.min(...a.items.map((i) => Date.parse(i.updatedAt))) - Math.min(...b.items.map((i) => Date.parse(i.updatedAt))));
  const ready = async (o: Order, id: string) => { await saveOrder(p.store, identity(p.device), setItemStatus(o, id, 'ready', new Date())); p.onSyncNow(); await reload(); };

  return (
    <div className="flex h-full flex-col bg-neutral-900 text-white">
      <header className="flex flex-wrap items-center gap-3 border-b border-white/10 px-4 py-2">
        <ChefHat size={28} />
        <div className="font-display text-xl font-bold">Cozinha · {p.device.store.name}</div>
        <div className="flex gap-2">
          {['Todas', ...stations].map((s) => (
            <button key={s} onClick={() => setStation(s === 'Todas' ? null : s)} className={clsx('h-10 rounded-full px-4 font-medium', (station ?? 'Todas') === s ? 'bg-white text-black' : 'bg-white/10')}>{s}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2"><StatusPill sync={p.sync} /><button onClick={() => setLeaving(true)} className="h-10 rounded-lg px-3 text-sm hover:bg-white/10">Sair do ecrã da cozinha</button></div>
      </header>
      <main className="grid flex-1 auto-rows-min grid-cols-1 gap-3 overflow-y-auto p-3 sm:grid-cols-2 lg:grid-cols-4">
        {!cards.length && <p className="col-span-full p-10 text-center text-xl text-white/60">Sem pedidos por preparar.</p>}
        {cards.map(({ o, items }) => {
          const age = minutes(items.filter((i) => i.status === 'sent').map((i) => i.updatedAt).sort()[0]!);
          return (
            <section key={o.id} aria-label={`Pedido ${o.label}`} className={clsx('rounded-2xl border-2 bg-neutral-800', age >= 20 ? 'border-red-500' : age >= 10 ? 'border-amber-400' : 'border-white/10')}>
              <div className="flex items-baseline justify-between border-b border-white/10 px-3 py-2"><span className="text-2xl font-bold">{o.label}</span><span className="num text-lg">{age} min</span></div>
              <ul className="p-2">
                {items.map((i) => (
                  <li key={i.id}>
                    <button disabled={i.status !== 'sent'} onClick={() => void ready(o, i.id)} className={clsx('w-full rounded-xl px-3 py-2 text-left', i.status === 'ready' ? 'opacity-40 line-through' : 'hover:bg-white/10')}>
                      <span className="text-xl font-semibold">{i.quantity.replace('.', ',')} × {i.description}</span>
                      {i.modifiers.length > 0 && <span className="block text-amber-300">{i.modifiers.map((m) => m.name).join(', ')}</span>}
                      {i.note && <span className="block italic text-amber-300">» {i.note}</span>}
                      {i.status === 'ready' && <span className="block text-sm text-green-400 no-underline">pronto</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>
      {leaving && (
        <div role="dialog" aria-modal="true" aria-label="Sair do ecrã da cozinha" className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-3">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 text-[var(--text)]">
            <div className="mb-3 flex items-center justify-between"><h2 className="text-xl font-bold">Voltar a ser caixa</h2><button aria-label="Fechar" onClick={() => setLeaving(false)}><X /></button></div>
            <ManagerApproval store={p.store} operator={{ id: '', name: 'Cozinha', role: 'cashier', pin: { salt: '', hash: '', iterations: 0 }, active: true }} why="Um gerente autoriza este equipamento a voltar a ser caixa." onApproved={() => p.onExit()} />
          </div>
        </div>
      )}
    </div>
  );
}
